import {
  Control,
  Controller,
  FieldErrors,
  UseFormTrigger,
  UseFormWatch,
} from 'react-hook-form';
import { HubSpotContact } from '../../../api/billing-types';
import { emailRegex } from '../../../utils/regular-expressions';
import {
  BillingRoles,
  isAbleToSeeUIElement,
} from '../../../utils/roles/client-roles';
import { Dispatch, SetStateAction } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  BillingKeys,
  setContactToBillingContact,
} from '../../../api/billing-api';
import { handleErrors } from '../../../utils/errors';

import { ModuleList } from '../../../shared/analytics/module-list';
import { useAnalytics } from '../../../shared/analytics/create-events';
import * as analytics from '../../../shared/analytics/analytics.json';
import { useStore } from '../../state/zustand-store';

import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import Grid from '@mui/material/Grid';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';

interface ContactInfoFormProps {
  control: Control<Partial<HubSpotContact>>;
  errors: FieldErrors<Partial<HubSpotContact>>;
  billingRoles: string[];
  formType: 'edit' | 'create';
  hubSpotCompanyId: number;
  hubSpotContacts: HubSpotContact[];
  trigger: UseFormTrigger<Partial<HubSpotContact>>;
  watch: UseFormWatch<Partial<HubSpotContact>>;
  setOpenAddContact?: Dispatch<SetStateAction<boolean>>;
}

interface CreateContactInfoFormProps extends ContactInfoFormProps {
  formType: 'create';
  isPrimaryContact?: boolean;
}

interface EditContactInfoFormProps extends ContactInfoFormProps {
  formType: 'edit';
  isPrimaryContact: boolean;
}

export default function ContactInfoForm({
  control,
  errors,
  billingRoles,
  formType,
  isPrimaryContact,
  hubSpotCompanyId,
  hubSpotContacts,
  trigger,
  watch,
  setOpenAddContact,
}: CreateContactInfoFormProps | EditContactInfoFormProps) {
  const { createEvent } = useAnalytics();
  const { setSnackbar } = useStore();
  const queryClient = useQueryClient();

  const canChangePrimaryContact = isAbleToSeeUIElement(
    billingRoles,
    BillingRoles.canChangePrimaryContact.value
  );

  const canEditContacts =
    isAbleToSeeUIElement(billingRoles, BillingRoles.canEditContacts.value) ||
    formType === 'create';

  const { mutate, isPending } = useMutation({
    mutationFn: (contactEmail: string) =>
      setContactToBillingContact(contactEmail, hubSpotCompanyId),
    onSuccess: () => {
      setSnackbar({
        open: true,
        severity: 'success',
        message: 'Contact updated to billing contact successfully!',
      });

      queryClient.invalidateQueries({
        queryKey: [BillingKeys.HubSpotContacts],
      });
      if (setOpenAddContact) {
        setOpenAddContact(false);
      }
    },
    onError: (error) => {
      handleErrors({
        error,
        snackbarMessage:
          'There was an issue updating the contact to a billing contact. Please refresh and try again.',
      });
    },
    onSettled: () => {
      createEvent(analytics.billing.billing_contacts.update_hubspot_contact, {
        module: ModuleList.modules.billing.name,
        section: ModuleList.modules.billing.sections.billingContacts,
      });
    },
  });

  const contactEmail = watch('properties.email') || '';

  return (
    <Tooltip
      arrow
      title={
        canEditContacts
          ? ''
          : `You need the permission "${BillingRoles.canEditContacts.name}" to change the primary billing contact.`
      }
    >
      <Grid container spacing={2}>
        {formType === 'edit' && (
          <Grid
            size={{ xs: 12 }}
            sx={{ display: 'flex', flexDirection: 'row', ml: 1 }}
          >
            <Controller
              name={'properties.primary_billing_contact_'}
              control={control}
              render={({ field: props }) => (
                <Tooltip
                  arrow
                  title={
                    canChangePrimaryContact
                      ? 'This is the billing contact for your Google Workspace subscription. This contact will be used for any additional licenses and will be the person Promevo will contact with any payment-related questions.'
                      : `You need the permission "${BillingRoles.canChangePrimaryContact.name}" to change the primary billing contact.`
                  }
                >
                  <FormControlLabel
                    control={
                      <Checkbox
                        {...props}
                        checked={props.value}
                        onChange={(e) => props.onChange(e.target.checked)}
                        disabled={!canChangePrimaryContact || isPrimaryContact}
                      />
                    }
                    label="Primary Contact"
                  />
                </Tooltip>
              )}
            />
          </Grid>
        )}
        <Grid
          size={{ xs: 12, md: 6 }}
          sx={{ display: 'flex', flexDirection: 'column' }}
        >
          <Stack flexDirection={'row'} gap={2}>
            <Controller
              name={'properties.email'}
              control={control}
              rules={{
                required: `Please provide an email address.`,
                pattern: {
                  value: emailRegex,
                  message: 'Please provide a valid email address.',
                },
                validate: (value) => {
                  const foundContact = hubSpotContacts.find(
                    (contact) =>
                      contact.properties.email === value &&
                      !contact.properties.billing_contact_
                  );
                  if (foundContact) {
                    return `This email is a contact within gPanel already but not a billing contact. Click the "Add" button to add this email as a billing contact within gPanel as well.`;
                  } else {
                    return true;
                  }
                },
              }}
              render={({ field }) => (
                <TextField
                  {...field}
                  inputProps={{
                    id: 'email',
                  }}
                  InputLabelProps={{
                    htmlFor: 'email',
                  }}
                  label="Email address"
                  required
                  autoComplete="email"
                  error={!!errors.properties?.email}
                  helperText={
                    errors.properties?.email
                      ? errors.properties?.email.message
                      : ' '
                  }
                  FormHelperTextProps={{
                    sx: { textWrap: 'nowrap' },
                  }}
                  onChange={(event) => {
                    field.onChange(event.target.value);
                    trigger('properties.email');
                  }}
                  fullWidth
                  disabled={!canEditContacts}
                />
              )}
            />
          </Stack>
        </Grid>

        {errors && errors.properties?.email?.type === 'validate' ? (
          <Grid
            size={{ xs: 12, md: 6 }}
            sx={{ display: 'flex', flexDirection: 'column' }}
          >
            <Button
              variant="contained"
              loading={isPending}
              sx={{ width: 'fit-content' }}
              onClick={() => {
                mutate(contactEmail);
              }}
              disabled={isPending}
            >
              Add
            </Button>
          </Grid>
        ) : (
          <Grid
            size={{ xs: 12, md: 6 }}
            sx={{ display: 'flex', flexDirection: 'column' }}
          ></Grid>
        )}

        <Grid
          size={{ xs: 12, md: 6 }}
          sx={{ display: 'flex', flexDirection: 'column' }}
        >
          <Controller
            name={'properties.firstName'}
            control={control}
            rules={{
              required: `Please provide a first name.`,
            }}
            render={({ field }) => (
              <TextField
                {...field}
                inputProps={{
                  id: 'firstName',
                }}
                InputLabelProps={{
                  htmlFor: 'firstName',
                }}
                label="First Name"
                fullWidth
                required
                autoComplete="given-name"
                error={!!errors.properties?.firstName}
                helperText={
                  errors.properties?.firstName
                    ? errors.properties?.firstName.message
                    : ' '
                }
                disabled={!canEditContacts}
              />
            )}
          />
        </Grid>

        <Grid
          size={{ xs: 12, md: 6 }}
          sx={{ display: 'flex', flexDirection: 'column' }}
        >
          <Controller
            name={'properties.lastName'}
            control={control}
            rules={{
              required: `Please provide a last name.`,
            }}
            render={({ field }) => (
              <TextField
                {...field}
                inputProps={{
                  id: 'lastName',
                }}
                InputLabelProps={{
                  htmlFor: 'lastName',
                }}
                label="Last Name"
                fullWidth
                required
                autoComplete="family-name"
                error={!!errors.properties?.lastName}
                helperText={
                  errors.properties?.lastName
                    ? errors.properties?.lastName.message
                    : ' '
                }
                disabled={!canEditContacts}
              />
            )}
          />
        </Grid>
        <Grid
          size={{ xs: 12, md: 6 }}
          sx={{ display: 'flex', flexDirection: 'column' }}
        >
          <Controller
            name={'properties.phone'}
            control={control}
            rules={{
              required: `Please provide a phone number.`,
            }}
            render={({ field }) => (
              <TextField
                {...field}
                inputProps={{
                  id: 'phone',
                }}
                InputLabelProps={{
                  htmlFor: 'phone',
                }}
                label="Phone Number"
                fullWidth
                required
                autoComplete="tel"
                error={!!errors.properties?.phone}
                helperText={
                  errors.properties?.phone
                    ? errors.properties?.phone.message
                    : ' '
                }
                disabled={!canEditContacts}
              />
            )}
          />
        </Grid>
      </Grid>
    </Tooltip>
  );
}
